import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  Dumbbell,
  Waves,
  Target,
  Users,
  ArrowRight,
  CheckCircle2,
  Search,
  CalendarCheck,
  Wallet,
  QrCode,
  MapPin,
  Shield,
  Menu,
  X
} from 'lucide-react';

const LandingPage: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const styles = {
    container: { maxWidth: '1200px', margin: '0 auto', padding: '0 24px' },
    nav: { borderBottom: '1px solid #e8eaed', padding: '16px 0', position: 'sticky' as const, top: 0, zIndex: 1000, backdropFilter: 'blur(10px)', backgroundColor: 'rgba(255, 255, 255, 0.95)' },
    navContainer: { maxWidth: '1200px', margin: '0 auto', padding: '0 16px', display: 'flex', justifyContent: isMobile ? 'flex-start' : 'space-between', alignItems: 'center', gap: isMobile ? '12px' : '0' },
    logo: { fontSize: isMobile ? '20px' : '24px', fontWeight: 700, color: '#14B8A6', textDecoration: 'none', letterSpacing: '-0.5px' },
    navLinks: { display: 'flex', gap: '32px', alignItems: 'center' },
    mobileMenu: { position: 'fixed' as const, top: 0, left: 0, width: '80%', maxWidth: '400px', height: '100vh', background: '#ffffff', zIndex: 2000, padding: '80px 24px 24px', display: 'flex', flexDirection: 'column' as const, gap: '24px' },
    mobileMenuOverlay: { position: 'fixed' as const, top: 0, left: 0, width: '100%', height: '100vh', background: 'rgba(0,0,0,0.5)', zIndex: 1999 },
    hamburger: { background: 'none', border: 'none', cursor: 'pointer', padding: '8px' },
    closeButton: { position: 'absolute' as const, top: '16px', right: '16px', background: 'none', border: 'none', cursor: 'pointer', padding: '8px' },
    navLink: { color: '#5f6368', textDecoration: 'none', fontSize: '14px', fontWeight: 500, transition: 'color 0.2s' },
    primaryButton: { background: '#14B8A6', color: '#ffffff', padding: '12px 24px', borderRadius: '24px', textDecoration: 'none', fontSize: '14px', fontWeight: 600, border: 'none', cursor: 'pointer', transition: 'all 0.2s', display: 'inline-flex', alignItems: 'center', gap: '8px', boxShadow: '0 1px 2px 0 rgba(60,64,67,0.3), 0 1px 3px 1px rgba(60,64,67,0.15)' },
    secondaryButton: { background: '#ffffff', color: '#14B8A6', padding: '12px 24px', borderRadius: '24px', textDecoration: 'none', fontSize: '14px', fontWeight: 600, border: '1px solid #dadce0', cursor: 'pointer', transition: 'all 0.2s', display: 'inline-flex', alignItems: 'center', gap: '8px' },
  };

  const categories = [
    { icon: <Dumbbell size={28} color="#14B8A6" />, name: 'Gym' },
    { icon: <Target size={28} color="#14B8A6" />, name: 'Yoga' },
    { icon: <Waves size={28} color="#14B8A6" />, name: 'Swimming' },
    { icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#14B8A6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>, name: 'Turf & Courts' },
    { icon: <Users size={28} color="#14B8A6" />, name: 'CrossFit' },
    { icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#14B8A6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>, name: 'Dance' },
    { icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#14B8A6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12" y2="18"/></svg>, name: 'Online Sessions' },
    { icon: <Users size={28} color="#14B8A6" />, name: 'Personal Training' },
  ];

  const howItWorks = [
    { icon: <Search size={28} color="#ffffff" />, step: '01', title: 'Discover', desc: 'Browse gyms, yoga studios, turfs, swimming pools and more near you.' },
    { icon: <CalendarCheck size={28} color="#ffffff" />, step: '02', title: 'Book Instantly', desc: 'Pick your activity, choose a time slot, and confirm in seconds.' },
    { icon: <Wallet size={28} color="#ffffff" />, step: '03', title: 'Pay Per Session', desc: 'Pay only for what you use from your HUSLRZ Wallet. No memberships.' },
    { icon: <QrCode size={28} color="#ffffff" />, step: '04', title: 'Check In & Go', desc: 'Show your QR code at the door, get verified, and start your session.' },
  ];

  const audienceCards = [
    {
      title: 'For You',
      subtitle: 'Fitness enthusiasts',
      desc: 'Access any gym, yoga studio, swimming pool, turf or court in your city. Pay only when you go. No membership, no lock-in.',
      cta: 'Take the Fitness Survey',
      ctaLink: '/user-survey',
      highlights: ['10+ activity categories', 'Pay-per-session from ₹59', 'Wallet with bonus credits', 'Earn rewards & streaks'],
      gradient: 'linear-gradient(135deg, #14B8A6 0%, #0891B2 100%)',
    },
    {
      title: 'For Partners',
      subtitle: 'Gym & facility owners',
      desc: 'Fill your empty slots with verified, paying users. Zero cost to join. Automated payouts. Real-time dashboard.',
      cta: 'Register as Partner',
      ctaLink: '/gym-survey',
      highlights: ['Zero joining fee', 'Verified paying users', 'Automated bi-weekly payouts', 'Partner dashboard & analytics'],
      gradient: 'linear-gradient(135deg, #0891B2 0%, #0E7490 100%)',
    },
    {
      title: 'For Corporates',
      subtitle: 'HR & wellness teams',
      desc: 'Flexible employee wellness that actually works. Real-time tracking, multi-activity access, and zero-risk pilot programs.',
      cta: 'Explore Wellness Plans',
      ctaLink: '/corporate-survey',
      highlights: ['Multi-activity flexibility', 'Real-time utilisation reports', 'Zero-risk pilot available', 'Dedicated account manager'],
      gradient: 'linear-gradient(135deg, #0E7490 0%, #155E75 100%)',
    },
  ];

  return (
    <>
      <Helmet>
        <title>HUSLRZ — Fitness Without Commitment | Pay-Per-Use Fitness Marketplace</title>
        <meta name="description" content="Book gym, yoga, swimming, turf & 10+ activities across India. Pay only when you go. No membership. No lock-in. Download HUSLRZ today." />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
      </Helmet>

      <div style={{ fontFamily: 'Google Sans, Roboto, Arial, sans-serif', color: '#202124' }}>
        {/* Navigation */}
        <nav style={styles.nav}>
          <div style={styles.navContainer}>
            {isMobile && (
              <button style={styles.hamburger} onClick={() => setMobileMenuOpen(true)}>
                <Menu size={24} color="#202124" />
              </button>
            )}
            <Link to="/" style={styles.logo}>HUSLRZ</Link>
            {!isMobile && (
              <div style={styles.navLinks}>
                <Link to="/partners" style={styles.navLink}>For Partners</Link>
                <Link to="/corporate" style={styles.navLink}>For Corporates</Link>
                <Link to="/about" style={styles.navLink}>About</Link>
                <Link to="/contact" style={styles.navLink}>Contact</Link>
                <Link to="/login" style={{ ...styles.navLink, color: '#14B8A6', fontWeight: 600 }}>Login</Link>
                <Link to="/signup" style={styles.primaryButton}>
                  Get Started <ArrowRight size={16} />
                </Link>
              </div>
            )}
          </div>
        </nav>

        {/* Mobile Menu */}
        {isMobile && mobileMenuOpen && (
          <>
            <div style={styles.mobileMenuOverlay} onClick={() => setMobileMenuOpen(false)} />
            <div style={styles.mobileMenu}>
              <button style={styles.closeButton} onClick={() => setMobileMenuOpen(false)}>
                <X size={24} color="#202124" />
              </button>
              <Link to="/partners" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0' }} onClick={() => setMobileMenuOpen(false)}>For Partners</Link>
              <Link to="/corporate" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0' }} onClick={() => setMobileMenuOpen(false)}>For Corporates</Link>
              <Link to="/about" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0' }} onClick={() => setMobileMenuOpen(false)}>About</Link>
              <Link to="/contact" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0' }} onClick={() => setMobileMenuOpen(false)}>Contact</Link>
              <Link to="/login" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0', color: '#14B8A6', fontWeight: 600 }} onClick={() => setMobileMenuOpen(false)}>Login</Link>
              <Link to="/signup" style={{ ...styles.primaryButton, width: '100%', justifyContent: 'center', padding: '16px 24px', fontSize: '16px', marginTop: '16px' }} onClick={() => setMobileMenuOpen(false)}>
                Get Started <ArrowRight size={18} />
              </Link>
            </div>
          </>
        )}

        {/* Hero Section */}
        <section style={{ padding: isMobile ? '48px 16px' : '80px 24px', textAlign: 'center' as const, background: '#ffffff' }}>
          <div style={styles.container}>
            <div className="animate-fade-in-up" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#e6f7f5', color: '#14B8A6', padding: isMobile ? '6px 12px' : '8px 16px', borderRadius: '24px', fontSize: isMobile ? '11px' : '14px', fontWeight: 500, marginBottom: '28px', whiteSpace: 'nowrap' as const }}>
              <MapPin size={isMobile ? 12 : 16} style={{ flexShrink: 0 }} />
              {isMobile ? 'Chennai & Coimbatore' : 'Now in Chennai & Coimbatore — expanding soon'}
            </div>

            <h1 className="animate-fade-in-up delay-100" style={{ fontSize: 'clamp(32px, 8vw, 64px)', fontWeight: 400, color: '#202124', marginBottom: '20px', lineHeight: 1.15, letterSpacing: '-0.5px' }}>
              Fitness without<br />
              <span style={{ color: '#14B8A6' }}>commitment</span>
            </h1>

            <p className="animate-fade-in-up delay-200" style={{ fontSize: isMobile ? '16px' : '20px', color: '#5f6368', lineHeight: 1.6, maxWidth: '680px', margin: '0 auto 32px', fontWeight: 400 }}>
              Book gym, yoga, swimming, turf and 10+ activities across your city.
              Pay only when you go. No membership. No lock-in.
            </p>

            <div className="animate-fade-in-up delay-300" style={{ display: 'flex', gap: isMobile ? '12px' : '16px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '32px' }}>
              <Link to="/user-survey" style={{ ...styles.primaryButton, padding: isMobile ? '14px 24px' : '16px 32px', fontSize: '16px', flex: isMobile ? '1 1 100%' : 'none', justifyContent: 'center' }}>
                Get Started Free <ArrowRight size={18} />
              </Link>
              <Link to="/partners" style={{ ...styles.secondaryButton, padding: isMobile ? '14px 24px' : '16px 32px', fontSize: '16px', flex: isMobile ? '1 1 100%' : 'none', justifyContent: 'center' }}>
                List Your Facility
              </Link>
            </div>

            <div style={{ display: 'flex', gap: isMobile ? '12px' : '32px', justifyContent: 'center', flexWrap: 'wrap', fontSize: isMobile ? '12px' : '14px', color: '#5f6368' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><CheckCircle2 size={isMobile ? 14 : 16} color="#34A853" /> No membership required</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><CheckCircle2 size={isMobile ? 14 : 16} color="#34A853" /> Sessions from ₹59</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><CheckCircle2 size={isMobile ? 14 : 16} color="#34A853" /> 10+ activity types</div>
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section style={{ padding: isMobile ? '36px 16px' : '48px 24px', background: '#f8f9fa' }}>
          <div style={styles.container}>
            <h2 style={{ fontSize: 'clamp(24px, 5vw, 40px)', fontWeight: 400, color: '#202124', marginBottom: '12px', textAlign: 'center' as const, letterSpacing: '-0.3px' }}>
              One app, endless possibilities
            </h2>
            <p style={{ fontSize: isMobile ? '15px' : '18px', color: '#5f6368', textAlign: 'center' as const, maxWidth: '600px', margin: '0 auto 40px' }}>
              Explore a wide range of fitness and sports activities near you
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: isMobile ? '12px' : '16px' }}>
              {categories.map((cat, i) => (
                <div key={i} style={{ background: '#ffffff', borderRadius: '16px', padding: isMobile ? '20px 16px' : '28px 24px', textAlign: 'center' as const, border: '1px solid #e8eaed', transition: 'all 0.2s' }}>
                  <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center' }}>{cat.icon}</div>
                  <div style={{ fontSize: isMobile ? '14px' : '15px', fontWeight: 500, color: '#202124' }}>{cat.name}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section style={{ padding: isMobile ? '36px 16px' : '48px 24px', background: '#ffffff' }}>
          <div style={styles.container}>
            <h2 style={{ fontSize: 'clamp(24px, 5vw, 40px)', fontWeight: 400, color: '#202124', marginBottom: '12px', textAlign: 'center' as const, letterSpacing: '-0.3px' }}>
              How HUSLRZ works
            </h2>
            <p style={{ fontSize: isMobile ? '15px' : '18px', color: '#5f6368', textAlign: 'center' as const, maxWidth: '550px', margin: '0 auto 48px' }}>
              From discovery to check-in, in under 60 seconds
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', gap: isMobile ? '20px' : '24px' }}>
              {howItWorks.map((step, i) => (
                <div key={i} style={{ textAlign: 'center' as const }}>
                  <div style={{ width: '64px', height: '64px', borderRadius: '16px', background: 'linear-gradient(135deg, #14B8A6 0%, #0891B2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                    {step.icon}
                  </div>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: '#14B8A6', textTransform: 'uppercase' as const, letterSpacing: '1px', marginBottom: '8px' }}>Step {step.step}</div>
                  <h3 style={{ fontSize: '18px', fontWeight: 500, color: '#202124', marginBottom: '8px' }}>{step.title}</h3>
                  <p style={{ fontSize: '14px', color: '#5f6368', lineHeight: 1.6, margin: 0 }}>{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Audience Sections */}
        <section style={{ padding: isMobile ? '36px 16px' : '48px 24px', background: '#f8f9fa' }}>
          <div style={styles.container}>
            <h2 style={{ fontSize: 'clamp(24px, 5vw, 40px)', fontWeight: 400, color: '#202124', marginBottom: '12px', textAlign: 'center' as const, letterSpacing: '-0.3px' }}>
              Built for everyone in fitness
            </h2>
            <p style={{ fontSize: isMobile ? '15px' : '18px', color: '#5f6368', textAlign: 'center' as const, maxWidth: '600px', margin: '0 auto 48px' }}>
              Whether you work out, run a facility, or manage employee wellness
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: isMobile ? '20px' : '24px' }}>
              {audienceCards.map((card, i) => (
                <div key={i} style={{ background: '#ffffff', borderRadius: '20px', overflow: 'hidden', border: '1px solid #e8eaed' }}>
                  <div style={{ background: card.gradient, padding: isMobile ? '24px 20px' : '32px 28px', color: '#ffffff' }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '1px', opacity: 0.85, marginBottom: '4px' }}>{card.subtitle}</div>
                    <h3 style={{ fontSize: isMobile ? '22px' : '26px', fontWeight: 500, margin: '0 0 8px' }}>{card.title}</h3>
                    <p style={{ fontSize: '14px', opacity: 0.9, lineHeight: 1.5, margin: 0 }}>{card.desc}</p>
                  </div>
                  <div style={{ padding: isMobile ? '20px' : '28px' }}>
                    <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px' }}>
                      {card.highlights.map((h, j) => (
                        <li key={j} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', fontSize: '14px', color: '#374151' }}>
                          <CheckCircle2 size={16} color="#14B8A6" style={{ flexShrink: 0 }} /> {h}
                        </li>
                      ))}
                    </ul>
                    <a href={card.ctaLink} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', width: '100%', padding: '14px', borderRadius: '12px', background: '#14B8A6', color: '#ffffff', textDecoration: 'none', fontSize: '15px', fontWeight: 600, border: 'none', transition: 'all 0.2s' }}>
                      {card.cta} <ArrowRight size={16} />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Trust Section */}
        <section style={{ padding: isMobile ? '36px 16px' : '48px 24px', background: '#ffffff' }}>
          <div style={styles.container}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: isMobile ? '24px' : '40px', textAlign: 'center' as const }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}><Shield size={32} color="#14B8A6" /></div>
                <h3 style={{ fontSize: '18px', fontWeight: 500, color: '#202124', marginBottom: '8px' }}>Secure Payments</h3>
                <p style={{ fontSize: '14px', color: '#5f6368', lineHeight: 1.6, margin: 0 }}>All transactions are encrypted and processed through RBI-compliant payment gateways.</p>
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}><CheckCircle2 size={32} color="#14B8A6" /></div>
                <h3 style={{ fontSize: '18px', fontWeight: 500, color: '#202124', marginBottom: '8px' }}>Verified Partners</h3>
                <p style={{ fontSize: '14px', color: '#5f6368', lineHeight: 1.6, margin: 0 }}>Every facility is physically verified for safety, hygiene, and quality before listing.</p>
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}><Wallet size={32} color="#14B8A6" /></div>
                <h3 style={{ fontSize: '18px', fontWeight: 500, color: '#202124', marginBottom: '8px' }}>Instant Refunds</h3>
                <p style={{ fontSize: '14px', color: '#5f6368', lineHeight: 1.6, margin: 0 }}>Cancel up to 24 hours before your session for a full refund to your HUSLRZ Wallet.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section style={{ padding: isMobile ? '36px 16px' : '56px 24px', background: 'linear-gradient(135deg, #14B8A6 0%, #0891B2 100%)', textAlign: 'center' as const, color: '#ffffff' }}>
          <div style={styles.container}>
            <h2 style={{ fontSize: 'clamp(28px, 6vw, 48px)', fontWeight: 400, marginBottom: '16px', letterSpacing: '-0.5px' }}>
              Ready to move your way?
            </h2>
            <p style={{ fontSize: isMobile ? '16px' : '18px', marginBottom: '40px', opacity: 0.95, maxWidth: '550px', margin: '0 auto 40px' }}>
              Join thousands of fitness enthusiasts, facility partners, and companies already on HUSLRZ.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href="/user-survey" style={{ background: '#ffffff', color: '#14B8A6', padding: isMobile ? '14px 28px' : '16px 32px', borderRadius: '24px', textDecoration: 'none', fontSize: '16px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', flex: isMobile ? '1 1 100%' : 'none', justifyContent: 'center' }}>
                Get Started Free <ArrowRight size={18} />
              </a>
              <Link to="/contact" style={{ background: 'transparent', color: '#ffffff', padding: isMobile ? '14px 28px' : '16px 32px', borderRadius: '24px', textDecoration: 'none', fontSize: '16px', fontWeight: 600, border: '2px solid #ffffff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: isMobile ? '1 1 100%' : 'none' }}>
                Talk to Us
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer style={{ background: '#111827', padding: isMobile ? '40px 16px' : '64px 24px', color: '#ffffff' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: isMobile ? '32px' : '40px', marginBottom: '40px' }}>
              <div style={{ gridColumn: isMobile ? '1 / -1' : 'auto' }}>
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#14B8A6', marginBottom: '12px' }}>HUSLRZ</div>
                <p style={{ fontSize: '14px', color: '#9CA3AF', lineHeight: 1.6, margin: 0 }}>India's pay-per-use fitness marketplace. Fitness without commitment.</p>
              </div>
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px', color: '#ffffff' }}>Platform</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li style={{ marginBottom: '10px' }}><a href="/gym-survey" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>For Partners</a></li>
                  <li style={{ marginBottom: '10px' }}><a href="/corporate-survey" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>For Corporates</a></li>
                  <li style={{ marginBottom: '10px' }}><Link to="/about" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>About Us</Link></li>
                  <li><Link to="/contact" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>Contact</Link></li>
                </ul>
              </div>
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px', color: '#ffffff' }}>Legal</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li style={{ marginBottom: '10px' }}><Link to="/privacy" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>Privacy Policy</Link></li>
                  <li style={{ marginBottom: '10px' }}><Link to="/terms" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>Terms of Service</Link></li>
                  <li><Link to="/contact" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>Refund Policy</Link></li>
                </ul>
              </div>
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px', color: '#ffffff' }}>Get in Touch</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li style={{ marginBottom: '10px', fontSize: '14px', color: '#9CA3AF' }}>support@huslrz.com</li>
                  <li style={{ fontSize: '14px', color: '#9CA3AF' }}>Chennai, Tamil Nadu</li>
                </ul>
              </div>
            </div>
            <div style={{ borderTop: '1px solid #374151', paddingTop: '24px', textAlign: 'center' as const }}>
              <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>&copy; {new Date().getFullYear()} HUSLRZ. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LandingPage;
